import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Schema } from "@sparefoot/react-commons";
import { ScrollToTop } from "components/core/ScrollToTop";
import { Page, Section } from "components/core/Page";
import { HeroSection, CitiesGrid } from "components/organisms";
import { selectStateDetails } from "store/selectors/StatePages";
import { PAGE_TYPES } from "@sparefoot/tracking-events";
import usePageViewTracking from "hooks/tracking/usePageViewTracking";
import STATE_HERO_IMAGES from "./StateHeroImages";
import {
	buildStateLandingWebPageSchema,
	buildStateLandingWebSiteSchema,
	buildStateLandingBreadcrumbSchema
} from "./utils/buildStateLandingSchema";
import "./StateLandingPage.scss";

const stripContext = ({ "@context": _, ...rest }) => rest;

function StateLandingPage() {
	const {
		drilldownLinks,
		meta: { title, description, canonical },
		stateName,
		stateSlug,
		baseUrl,
	} = useSelector(selectStateDetails);
	usePageViewTracking(PAGE_TYPES.STATE_LANDING);

	const heroImages = STATE_HERO_IMAGES[stateName.toLowerCase().replace(/\s+/g, "-")] || null;

	const cities = useMemo(() => {
		if (!drilldownLinks || drilldownLinks.length === 0) return [];
		return drilldownLinks.map((d) => {
			const citySlug = d.city.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-");
			return {
				name: d.city,
				stateAbbr: d.state,
				url: `${baseUrl}/self-storage/${stateSlug}/${citySlug}/`,
				links: d.content || null
			};
		});
	}, [drilldownLinks, baseUrl, stateSlug]);

	const combinedSchema = useMemo(() => ({
		"@context": "https://schema.org",
		"@graph": [
			stripContext(buildStateLandingWebPageSchema({
				title,
				description,
				canonicalUrl: canonical,
				stateName
			})),
			stripContext(buildStateLandingWebSiteSchema({
				stateName,
				baseUrl
			})),
			stripContext(buildStateLandingBreadcrumbSchema({
				title,
				canonicalUrl: canonical,
				baseUrl
			}))
		]
	}), [title, description, canonical, stateName, baseUrl]);

	return (
		<Page
			pageType={PAGE_TYPES.STATE_LANDING}
			title={title}
			description={description}
			canonical={canonical}
			hideGlobalSchemas
		>
			<HeroSection stateName={stateName} heroImages={heroImages} />
			{cities.length > 0 && (
				<Section
					flush
					className="slp-cities-section"
				>
					<CitiesGrid cities={cities} />
				</Section>
			)}
			<Schema schema={combinedSchema} />
			<ScrollToTop />
		</Page>
	);
}

export default StateLandingPage;
